import * as yup from 'yup';
import { useExperiment, PROMO_CODE_CONTENT_KEY } from '@web/experiments';
import {
  nullableImageFromCMSSchema,
  requiredImageFromCMSSchema,
  richTextFromCMSSchema,
} from '../../cms';
import { schema as calloutSchema } from '../../cms/builders/quiz_callout';
import { addonSectionSchema } from './addon_sections';
import { addonModalSchema } from './addon_modal';
import { giftTypeSchema } from './gift_type';

export const quizNextButtonSchema = yup
  .object({
    desktopCTA: yup.string(),
    mobileCTA: yup.string(),
  })
  .transform(value => {
    let { value: experimentValue } = useExperiment(value?.test_name ?? null);
    experimentValue = experimentValue || 'control';
    let ctaVariants = value;
    if (ctaVariants?.component === 'flagship_ab_test') {
      const experimentVariation = ctaVariants.variants.find(
        variant => variant.variant_name === experimentValue,
      );
      ctaVariants = experimentVariation?.blocks[0] ?? [];
    }
    return {
      desktopCTA: ctaVariants.desktopCTA || 'Continue',
      mobileCTA: ctaVariants.mobileCTA || 'Continue',
    };
  });

const usePromoCodeVariant = variants => {
  const { value: promoCode } = useExperiment(PROMO_CODE_CONTENT_KEY, '');

  return (
    variants.find(variant => variant.promoCode === promoCode) ??
    variants.find(variant => variant.promoCode === 'default')
  );
};

const useExperimentValues = value => {
  const question = value?.question?.[0];
  let { value: experimentValue } = useExperiment(question?.test_name ?? null);
  experimentValue = experimentValue || 'control';
  let questionVariants = question;
  const experimentVariation = questionVariants?.variants?.find(
    variant => variant.variant_name === experimentValue,
  );

  questionVariants = experimentVariation?.blocks[0] ?? question;
  return questionVariants;
};

const dropoffPathSchema = yup
  .object({
    headline: yup.string(),
    text: yup.string(),
    linkText: yup.string(),
    linkTarget: yup.string(),
    image: nullableImageFromCMSSchema,
  })
  .transform(value => {
    if (!value) return value;
    return {
      headline: value.headline,
      text: value.text,
      linkText: value.link_text,
      linkTarget: value.link_target?.url,
      image: value.image,
    };
  });

export const onboardingIntroSchema = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    headline: yup.string(),
    ctaButtonText: yup.string(),
    body: yup.string(),
    ollieLogo: requiredImageFromCMSSchema,
    mobileLogo: nullableImageFromCMSSchema,
    fontColor: yup.string(),
    popUpText: yup.string().nullable(),
    popUpBgColor: yup.string(),
    popUpFontColor: yup.string(),
    popUpAlignment: yup.string(),
    eyebrow: yup.string(),
    typography: yup.string(),
    bodyTypography: yup.string(),
    dropoffPath: dropoffPathSchema.nullable(),
  })
  .transform(value => {
    const question = useExperimentValues(value);
    return {
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      headline: question.headline,
      ctaButtonText: value.ctaButtonText || 'Continue',
      ollieLogo: value.ollieLogo,
      mobileLogo: question.mobileLogo,
      body: question.body,
      fontColor: question.fontColor || undefined,
      popUpText:
        usePromoCodeVariant(question.popUpTextVariants ?? [])?.message ||
        undefined,
      popUpBgColor: question.popUpBgColor || undefined,
      popUpFontColor: question.popUpFontColor || undefined,
      popUpAlignment: question.popUpAlignment || undefined,
      eyebrow: question.eyebrow,
      typography: question.typography || undefined,
      bodyTypography: question.body_typography || undefined,
      dropoffPath: question.dropoff_path?.[0] ?? null,
    };
  });

const mapInterstitialContentFromCMS = source => ({
  breedVariation: source.breed_variation || undefined,
  eyebrowText: source.eyebrow_text,
  headline: source.headline,
  subheadline: source.subheadline,
  body:
    source.body?.map(item =>
      item.component === 'list'
        ? { ...item, component: 'interstitial_list' }
        : item,
    ) ?? null,
  mobileImage: source.mobile_image,
  desktopImage: source.desktop_image,
});

const interstitialContentSchema = imageSchema =>
  yup
    .object({
      breedVariation: yup.string(),
      eyebrowText: yup.string(),
      headline: yup.string(),
      subheadline: richTextFromCMSSchema.nullable(),
      body: yup
        .array(
          yup.object({
            component: yup
              .string()
              .oneOf(['list', 'quiz_callout', 'interstitial_list'])
              .required(),
          }),
        )
        .nullable(),
      mobileImage: imageSchema,
      desktopImage: imageSchema,
    })
    .transform(value => (value ? mapInterstitialContentFromCMS(value) : value));

const mapInterstitialFromCMS = source => ({
  backgroundColor: source.background_color || undefined,
  fontColor: source.font_color || undefined,
  ctaButtonText: source.ctaButtonText || 'Continue',
  content: source.content ?? null,
});

const interstitialShape = imageSchema => ({
  backgroundColor: yup.string(),
  fontColor: yup.string(),
  ctaButtonText: yup.string(),
  content: yup.array(interstitialContentSchema(imageSchema)).nullable(),
});

export const interstitialSchema = yup
  .object(interstitialShape(nullableImageFromCMSSchema))
  .nullable()
  .transform(value => {
    if (!value) return null;
    return mapInterstitialFromCMS(value);
  });

export const pupName = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    labelName: yup.string(),
    subheadline: yup.string(),
    ctaButtonText: yup.string(),
    pupNameQuestionCaption: yup.string(),
    pupNameQuestionPrompt: yup.string(),
    pupNameCallout: calloutSchema.nullable(),
    showInterstitial: yup.boolean(),
    interstitial: interstitialSchema,
  })
  .transform(value => {
    const question = useExperimentValues(value);
    return {
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      ctaButtonText: value.ctaButtonText || 'Continue',
      subheadline: question.subheadline,
      pupNameQuestionCaption: question.caption,
      pupNameQuestionPrompt: question.prompt,
      labelName: question.labelName || "Your pup's name",
      pupNameCallout: question.callout?.[0] ?? null,
      showInterstitial: Boolean(question.show_interstitial),
      interstitial: question.interstitial?.[0] ?? null,
    };
  });

export const pupBreed = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    labelName: yup.string(),
    subheadline: yup.string(),
    ctaButtonText: yup.string(),
    pupBreedQuestionCaption: yup.string(),
    pupBreedQuestionPrompt: yup.string(),
    pupBreedCallout: calloutSchema.nullable(),
    featuredBreeds: yup.array(yup.string()),
    showInterstitial: yup.boolean(),
    interstitial: interstitialSchema,
  })
  .transform(value => {
    const question = useExperimentValues(value);
    return {
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      labelName: question.labelName || 'Search or select breed (up to two)',
      subheadline: question.subheadline,
      pupBreedQuestionCaption: question.caption,
      pupBreedQuestionPrompt: question.prompt,
      pupBreedCallout: question.callout?.[0] ?? null,
      featuredBreeds: (question.featured_breeds ?? [])
        .map(breed => breed?.name)
        .filter(Boolean),
      ctaButtonText: value.ctaButtonText || 'Continue',
      showInterstitial: Boolean(question.show_interstitial),
      interstitial: question.interstitial?.[0] ?? null,
    };
  });

export const pupGender = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    labelName: yup.string(),
    ctaButtonText: yup.string(),
    pupGenderQuestionSubheadline: yup.string(),
    pupGenderQuestionPrompt: yup.string(),
    pupGenderCallout: calloutSchema.nullable(),
    showInterstitial: yup.boolean(),
    interstitial: interstitialSchema,
  })
  .transform(value => {
    const question = useExperimentValues(value);
    return {
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      pupGenderQuestionSubheadline: question.subheadline,
      pupGenderQuestionPrompt: question.prompt,
      labelName: question.labelName || "Your pup's gender",
      pupGenderCallout: question.callout?.[0] ?? null,
      showInterstitial: Boolean(question.show_interstitial),
      interstitial: question.interstitial?.[0] ?? null,
    };
  });

export const pupStatus = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    labelName: yup.string(),
    ctaButtonText: yup.string(),
    pupStatusQuestionSubheadline: yup.string(),
    pupStatusQuestionPrompt: yup.string(),
    unspayedChipLabel: yup.string().required(),
    spayedChipLabel: yup.string().required(),
    pupStatusCallout: calloutSchema.nullable(),
    showInterstitial: yup.boolean(),
    interstitial: interstitialSchema,
  })
  .transform(value => {
    const question = useExperimentValues(value);
    return {
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      pupStatusQuestionSubheadline: question.subheadline,
      pupStatusQuestionPrompt: question.prompt,
      labelName: question.labelName || "Your pup's spay/neuter status",
      spayedChipLabel: '{{pupStatusTermUppercase}}',
      unspayedChipLabel: 'Not {{pupStatusTermUppercase}}',
      pupStatusCallout: question.callout?.[0] ?? null,
      showInterstitial: Boolean(question.show_interstitial),
      interstitial: question.interstitial?.[0] ?? null,
    };
  });

export const weight = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    weightLabel: yup.string(),
    idealWeightLabel: yup.string(),
    ctaButtonText: yup.string(),
    weightQuestionSubheadline: yup.string(),
    weightQuestionPrompt: yup.string(),
    puppyIdealWeightLabel: yup.string(),
    idealWeightHelp: yup.string(),
    weightCallout: yup.array(calloutSchema),
    showInterstitial: yup.boolean(),
    interstitial: interstitialSchema,
  })
  .transform(value => {
    const question = useExperimentValues(value);
    return {
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      ctaButtonText: value.ctaButtonText || 'Continue',
      weightQuestionSubheadline: question.subheadline,
      weightQuestionPrompt: question.prompt,
      weightLabel: question.weightLabel || "Pup's current weight in lbs",
      idealWeightLabel:
        question.idealWeightLabel || "Pup's ideal weight in lbs (optional)",
      puppyIdealWeightLabel:
        question.puppyIdealWeightLabel ||
        "Pup's ideal weight in lbs (optional)",
      idealWeightHelp:
        "If added, we'll use this weight to determine your pup's daily calories.",
      weightCallout: question.callout,
      showInterstitial: Boolean(question.show_interstitial),
      interstitial: question.interstitial?.[0] ?? null,
    };
  });

export const activity = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    activityQuestionPrompt: yup.string(),
    activityQuestionCallout: calloutSchema.nullable(),
    subheadline: yup.string(),
    levels: yup
      .array(
        yup.object({
          name: yup.string().required(),
          value: yup.string().required(),
          description: yup.string().required(),
          image: requiredImageFromCMSSchema,
        }),
      )
      .required(),
    showInterstitial: yup.boolean(),
    interstitial: interstitialSchema,
  })
  .transform(value => {
    const question = useExperimentValues(value);
    return {
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      subheadline: question.subheadline,
      activityQuestionPrompt: question.prompt,
      activityQuestionCallout: question.callout?.[0] ?? null,
      levels: [
        {
          name: question.low_activity_card_title,
          value: 'Low',
          image: question.low_activity_image,
          description: question.low_activity_card_description,
        },
        {
          name: question.medium_activity_card_title,
          value: 'Moderate',
          image: question.medium_activity_image,
          description: question.medium_activity_card_description,
        },
        {
          name: question.high_activity_card_title,
          value: 'High',
          image: question.high_activity_image,
          description: question.high_activity_card_description,
        },
      ],
      showInterstitial: Boolean(question.show_interstitial),
      interstitial: question.interstitial?.[0] ?? null,
    };
  });

export const allergies = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    allergyQuestionPrompt: yup.string(),
    allergyQuestionSubheadline: yup.string(),
    labelName: yup.string(),
    allergiesSelectionPrompt: yup.string(),
    allergiesSelectionCaption: yup.string(),
    allergiesSearchLabel: yup.string(),
    allergiesCallout: calloutSchema.nullable(),
    showInterstitial: yup.boolean(),
    interstitial: interstitialSchema,
  })
  .transform(value => {
    const question = useExperimentValues(value);
    return {
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      allergyQuestionSubheadline: question.subheadline,
      allergyQuestionPrompt: question.prompt,
      labelName: question.labelName || 'Allergy',
      allergiesSelectionPrompt: question.selection_prompt,
      allergiesSelectionCaption: question.selection_caption,
      allergiesSearchLabel: question.selection_label_name || 'Other',
      allergiesSelectionSubheadline: question.selection_subheadline,
      allergiesCallout: question.callout?.[0] ?? null,
      showInterstitial: Boolean(question.show_interstitial),
      interstitial: question.interstitial?.[0] ?? null,
    };
  });

export const giftWithPurchase = yup
  .object({
    mobileImage: nullableImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    imagePillText: yup.string(),
    imagePillVariant: yup.string(),
    headline: yup.string(),
    subheadline: yup.string(),
    giftTypes: yup.array(giftTypeSchema),
    body: yup.array(yup.mixed()),
    cardsPerRow: yup.number(),
  })
  .transform(({ body, ...value }) => {
    const question = useExperimentValues(value);
    return {
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      imagePillText: question.imagePill,
      imagePillVariant: question.imagePillVariant || undefined,
      headline: question.headline,
      subheadline: question.subheadline,
      giftTypes: question.giftTypes || undefined,
      body: question.body,
      cardsPerRow: Number(question.cards_per_row) || undefined,
    };
  });

export const extras = yup
  .object({
    mobileImage: nullableImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    desktopCtaText: yup.string(),
    headline: yup.string(),
    subheadline: yup.string(),
    addonSections: yup.array(addonSectionSchema).required(),
    body: yup.array(yup.mixed()),
    modals: yup.array(addonModalSchema),
    pillText: yup.string(),
    pillVariant: yup.string(),
  })
  .transform(({ body, ...value }) => {
    const question = useExperimentValues(value);
    return {
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      desktopCtaText: value.ctaButtonText || 'Continue',
      headline: question.headline,
      subheadline: question.subheadline,
      addonSections: question.addon_sections,
      body: question.body,
      modals: question.modals,
      pillText: question.pillText,
      pillVariant: question.pillVariant,
    };
  });

export const userInfoSchema = yup
  .object({
    headline: yup.string(),
    subheadline: yup.string(),
    eyebrowText: yup.string(),
    emailLabel: yup.string().required(),
    firstNameLabel: yup.string().required(),
    ctaButtonText: yup.string(),
    image: requiredImageFromCMSSchema,
    eyebrowVariant: yup.string(),
  })
  .transform(value => {
    const question = useExperimentValues(value);
    return {
      headline: question.headline,
      subheadline: question.subheadline,
      eyebrowText: value?.question[0]?.eyebrow_text || value?.eyebrow_text,
      emailLabel: question.email_label || 'Email Address',
      firstNameLabel: question.first_name_label || 'Your Name',
      ctaButtonText: question.continue || 'Continue',
      image: question.image,
      eyebrowVariant: question.eyebrowVariant || undefined,
    };
  });

const abandonerMenuContentSchema = yup
  .object({
    headline: yup.string().required(),
    subheadline: yup.string().required(),
    multipetCallout: yup.string(),
    primaryCtaText: yup.string().required(),
    secondaryCtaText: yup.string(),
  })
  .transform(value => ({
    headline: value.headline,
    subheadline: value.subheadline,
    multipetCallout: value.multipetCallout,
    primaryCtaText: value.primaryCtaText,
    secondaryCtaText: value.secondaryCtaText,
  }));

export const quizAbandonerMenuSchema = yup
  .object({
    ollieLogo: requiredImageFromCMSSchema,
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    imagePillText: yup.string(),
    imagePillVariant: yup.string(),
    quizContent: yup.array(abandonerMenuContentSchema).required(),
    planContent: yup.array(abandonerMenuContentSchema).required(),
    checkoutContent: yup.array(abandonerMenuContentSchema).required(),
  })
  .transform(({ body, ...value }) => {
    const question = useExperimentValues(value);
    return {
      ollieLogo: value.ollieLogo,
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      imagePillText: question.pillText,
      imagePillVariant: question.pillVariant || undefined,
      quizContent: question.quizContent,
      planContent: question.planContent,
      checkoutContent: question.checkoutContent,
    };
  });
